import type { Organization, WebSite, WithContext } from 'schema-dts';
import { Locale as DateFnsLocale } from 'date-fns/types';
import { Shared } from '@strapi/strapi';
import { de, enUS, fr, frCH } from 'date-fns/locale';

export enum Project {
  EMobilityAssistant = 'e-mobility-assistant',
  ElectricityTariff = 'electricity-tariff',
  Energy = 'energy',
  Styleguide = 'styleguide',
  Website = 'website',
}

export enum Locale {
  De = 'de',
  DeCH = 'de-CH',
  En = 'en',
  EnUS = 'en-US',
  Fr = 'fr',
  FrCH = 'fr-CH',
}

export enum LocalePrefix {
  De = 'de_de',
  DeCH = 'ch_de',
  En = 'int_en',
  EnUS = 'usa_en',
  Fr = 'fr_fr',
  FrCH = 'ch_fr',
}

export const LocaleToPrefixMap: { [key in Locale]: LocalePrefix } = {
  [Locale.De]: LocalePrefix.De,
  [Locale.DeCH]: LocalePrefix.DeCH,
  [Locale.En]: LocalePrefix.En,
  [Locale.EnUS]: LocalePrefix.EnUS,
  [Locale.Fr]: LocalePrefix.Fr,
  [Locale.FrCH]: LocalePrefix.FrCH,
};

export const PrefixToLocaleMap: { [key in LocalePrefix]: Locale } = {
  [LocalePrefix.De]: Locale.De,
  [LocalePrefix.DeCH]: Locale.DeCH,
  [LocalePrefix.En]: Locale.En,
  [LocalePrefix.EnUS]: Locale.EnUS,
  [LocalePrefix.Fr]: Locale.Fr,
  [LocalePrefix.FrCH]: Locale.FrCH,
};

export const PrefixToDateFns: { [key in LocalePrefix]: DateFnsLocale } = {
  [LocalePrefix.De]: de,
  [LocalePrefix.DeCH]: de,
  [LocalePrefix.En]: enUS,
  [LocalePrefix.EnUS]: enUS,
  [LocalePrefix.Fr]: fr,
  [LocalePrefix.FrCH]: frCH,
};

export const PrefixToCurrencyMap: { [key in LocalePrefix]: string } = {
  [LocalePrefix.De]: 'EUR',
  [LocalePrefix.DeCH]: 'CHF',
  [LocalePrefix.En]: 'USD',
  [LocalePrefix.EnUS]: 'USD',
  [LocalePrefix.Fr]: 'EUR',
  [LocalePrefix.FrCH]: 'CHF',
};

export const DefaultLocalePrefix = LocalePrefix.De as const;

export enum Audience {
  BusinessCustomers = 'BusinessCustomers',
  BusinessPartners = 'BusinessPartners',
  Default = 'Default',
  OurCompany = 'OurCompany',
  PrivateCustomers = 'PrivateCustomers',
}

export const LocaleToAudienceMap: { [key in LocalePrefix]: Audience[] } = {
  [LocalePrefix.En]: [Audience.Default],
  [LocalePrefix.De]: [
    Audience.BusinessCustomers,
    Audience.BusinessPartners,
    Audience.OurCompany,
    Audience.PrivateCustomers,
  ],
  [LocalePrefix.DeCH]: [
    Audience.BusinessCustomers,
    Audience.BusinessPartners,
    Audience.OurCompany,
    Audience.PrivateCustomers,
  ],
  [LocalePrefix.EnUS]: [Audience.Default],
  [LocalePrefix.FrCH]: [Audience.Default],
  [LocalePrefix.Fr]: [Audience.Default],
};

export function getFavoredAudience(audiences: Audience[]) {
  if (audiences === undefined) return Audience.Default;
  return audiences.includes(Audience.PrivateCustomers) ? Audience.PrivateCustomers : audiences[0];
}

const PROJECTS_WITHOUT_AUDIENCES: Project[] = [Project.ElectricityTariff, Project.Energy, Project.Styleguide];

export function hasAudiences(locale: LocalePrefix, project: Project) {
  if (PROJECTS_WITHOUT_AUDIENCES.includes(project)) {
    return false;
  }

  const audiences = LocaleToAudienceMap[locale];

  if (!audiences) {
    // Invalid or default locale was used
    return false;
  }

  if (audiences.length === 1 && audiences[0] === Audience.Default) {
    return false;
  }

  return true;
}

export function hasShop(locale: LocalePrefix, project: Project) {
  if ([Project.Website].includes(project)) {
    return [LocalePrefix.De, LocalePrefix.Fr, LocalePrefix.FrCH].includes(locale);
  }
  return false;
}

/**
 * An array that holds the information about all the projects that support multiple locales.
 */
export const PROJECTS_WITH_LOCALES = [Project.Energy, Project.Website] as const;

/** @see PROJECTS_WITH_LOCALES */
export type ProjectWithLocales = (typeof PROJECTS_WITH_LOCALES)[number];

/**
 * Determines whether the passed-in `project` is capable of rendering pages in multiple locales.
 *
 * @param project - The project to check.
 *
 * @returns - `true` if the project supports multiple locales, otherwise `false`.
 *
 * @typeguard
 */
export function hasLocales(project: Project): project is ProjectWithLocales {
  return PROJECTS_WITH_LOCALES.includes(project as ProjectWithLocales);
}

export function audiencePaysSalesTax(audience: Audience) {
  if ([Audience.BusinessCustomers, Audience.BusinessPartners].includes(audience)) {
    return false;
  }
  return true;
}

// Since we cannot store an empty string as slug in Strapi, we need to use a specific value for the page located at `/`
export const HomepageSlug = 'home';

export enum Social {
  Facebook = 'Facebook',
  Instagram = 'Instagram',
  LinkedIn = 'LinkedIn',
  Twitter = 'Twitter',
  YouTube = 'YouTube',
}

// TODO type `string` should be `PhosphorIconId` but this is causing issues when importing this file into a Strapi plugin
export const socialToIconMap: { [key in Social]: string } = {
  [Social.Facebook]: 'Facebook',
  [Social.Instagram]: 'Instagram',
  [Social.LinkedIn]: 'Linkedin',
  [Social.Twitter]: 'Twitter',
  [Social.YouTube]: 'Youtube',
};

export enum Breakpoint {
  Xs = 375,
  Sm = 576,
  Md = 768,
  Lg = 992,
  Xl = 1200,
}

export const breakpointToThemeKey: { [key in Breakpoint]: string } = {
  [Breakpoint.Lg]: 'lg',
  [Breakpoint.Md]: 'md',
  [Breakpoint.Sm]: 'sm',
  [Breakpoint.Xl]: 'xl',
  [Breakpoint.Xs]: 'xs',
} as const;

export const themeKeyToBreakpoint = {
  lg: Breakpoint.Lg,
  md: Breakpoint.Md,
  sm: Breakpoint.Sm,
  xl: Breakpoint.Xl,
  xs: Breakpoint.Xs,
} as const;

export enum PDFVariant {
  __Test__ = '__Test__',
  EMAResult = 'EMAResult',
}

const domains = [
  /http:\/\/localhost:\d+/,
  /https?:\/\/[\w-]+\.tmh\.cloud/,
  /https?:\/\/([\w-]+\.)*mobilityhouse\.com/,
  /https?:\/\/([\w-]+\.)*mobilityhouse-energy\.com/,
  /https?:\/\/[\w-]+\.mobilize-v2g\.(de|fr)/,
];

export const validCorsOrigin = new RegExp(domains.map(pattern => pattern.source).join('|'));

export const schemaOrganizationTMH: WithContext<Organization> = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'The Mobility House',
  url: 'https://www.mobilityhouse.com/',
  logo: 'https://www.mobilityhouse.com/web-v2/assets/images/favicon/organization-logo.svg',
};

export const schemaWebSite: WithContext<WebSite> = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: 'The Mobility House',
  url: 'https://www.mobilityhouse.com/',
};

export enum PageType {
  Car = 'Car',
  ChargepilotProductUpdateArticle = 'ChargepilotProductUpdateArticle',
  ElectricityTariffBlogArticle = 'ElectricityTariffBlogArticle',
  ElectricityTariffPage = 'ElectricityTariffPage',
  EnergyKnowledgeCenterArticle = 'EnergyKnowledgeCenterArticle',
  EnergyKnowledgeCenterWebinar = 'EnergyKnowledgeCenterWebinar',
  EnergyKnowledgeCenterWhitepaper = 'EnergyKnowledgeCenterWhitepaper',
  EnergyNewsArticle = 'EnergyNewsArticle',
  EnergyPage = 'EnergyPage',
  EnergyReferenceArticle = 'EnergyReferenceArticle',
  KnowledgeCenterArticle = 'KnowledgeCenterArticle',
  KnowledgeCenterWebinar = 'KnowledgeCenterWebinar',
  KnowledgeCenterWhitepaper = 'KnowledgeCenterWhitepaper',
  NewsArticle = 'NewsArticle',
  Page = 'Page',
  ReferenceArticle = 'ReferenceArticle',
  ShopCategoryPage = 'ShopCategoryPage',
  StyleguideArticle = 'StyleguideArticle',
  StyleguidePage = 'StyleguidePage',
}

export enum PageTypeWebsite {
  Car = 'Car',
  ChargepilotProductUpdateArticle = 'ChargepilotProductUpdateArticle',
  ElectricityTariffBlogArticle = 'ElectricityTariffBlogArticle',
  ElectricityTariffPage = 'ElectricityTariffPage',
  KnowledgeCenterArticle = 'KnowledgeCenterArticle',
  KnowledgeCenterWebinar = 'KnowledgeCenterWebinar',
  KnowledgeCenterWhitepaper = 'KnowledgeCenterWhitepaper',
  NewsArticle = 'NewsArticle',
  Page = 'Page',
  ReferenceArticle = 'ReferenceArticle',
  ShopCategoryPage = 'ShopCategoryPage',
  StyleguideArticle = 'StyleguideArticle',
  StyleguidePage = 'StyleguidePage',
}

export enum PageTypeEnergy {
  EnergyKnowledgeCenterArticle = 'EnergyKnowledgeCenterArticle',
  EnergyKnowledgeCenterWebinar = 'EnergyKnowledgeCenterWebinar',
  EnergyKnowledgeCenterWhitepaper = 'EnergyKnowledgeCenterWhitepaper',
  EnergyNewsArticle = 'EnergyNewsArticle',
  EnergyPage = 'EnergyPage',
  EnergyReferenceArticle = 'EnergyReferenceArticle',
}

export enum PageTypeUsedAsCanonicals {
  ChargepilotProductUpdateArticle = 'ChargepilotProductUpdateArticle',
  ElectricityTariffBlogArticle = 'ElectricityTariffBlogArticle',
  ElectricityTariffPage = 'ElectricityTariffPage',
  EnergyKnowledgeCenterArticle = 'EnergyKnowledgeCenterArticle',
  EnergyKnowledgeCenterWebinar = 'EnergyKnowledgeCenterWebinar',
  EnergyKnowledgeCenterWhitepaper = 'EnergyKnowledgeCenterWhitepaper',
  EnergyNewsArticle = 'EnergyNewsArticle',
  EnergyPage = 'EnergyPage',
  EnergyReferenceArticle = 'EnergyReferenceArticle',
  KnowledgeCenterArticle = 'KnowledgeCenterArticle',
  KnowledgeCenterWebinar = 'KnowledgeCenterWebinar',
  KnowledgeCenterWhitepaper = 'KnowledgeCenterWhitepaper',
  NewsArticle = 'NewsArticle',
  Page = 'Page',
  ReferenceArticle = 'ReferenceArticle',
  ShopCategoryPage = 'ShopCategoryPage',
  StyleguideArticle = 'StyleguideArticle',
  StyleguidePage = 'StyleguidePage',
}

export const pageTypeToEndpointMap: { [key in PageType]: string } = {
  [PageType.Car]: 'car-models',
  [PageType.ChargepilotProductUpdateArticle]: 'chargepilot-product-update-articles',
  [PageType.ElectricityTariffBlogArticle]: 'electricity-tariff/electricity-tariff-blog-articles',
  [PageType.ElectricityTariffPage]: 'electricity-tariff/electricity-tariff-pages',
  [PageType.EnergyKnowledgeCenterArticle]: 'energy/energy-knowledge-center-articles',
  [PageType.EnergyKnowledgeCenterWebinar]: 'energy/energy-knowledge-center-webinars',
  [PageType.EnergyKnowledgeCenterWhitepaper]: 'energy/energy-knowledge-center-whitepapers',
  [PageType.EnergyNewsArticle]: 'energy/energy-news-articles',
  [PageType.EnergyPage]: 'energy/energy-pages',
  [PageType.EnergyReferenceArticle]: 'energy/energy-reference-articles',
  [PageType.KnowledgeCenterArticle]: 'knowledge-center-articles',
  [PageType.KnowledgeCenterWebinar]: 'knowledge-center-webinars',
  [PageType.KnowledgeCenterWhitepaper]: 'knowledge-center-whitepapers',
  [PageType.NewsArticle]: 'news-articles',
  [PageType.Page]: 'pages',
  [PageType.ReferenceArticle]: 'reference-articles',
  [PageType.ShopCategoryPage]: 'shop-category-pages',
  [PageType.StyleguideArticle]: 'styleguide-articles',
  [PageType.StyleguidePage]: 'styleguide-pages',
};

export const pageTypeToApiNameMap = {
  [PageType.Car]: 'api::car.car-model',
  [PageType.ChargepilotProductUpdateArticle]: 'api::chargepilot.chargepilot-product-update-article',
  [PageType.ElectricityTariffBlogArticle]: 'api::electricity-tariff.electricity-tariff-blog-article',
  [PageType.ElectricityTariffPage]: 'api::electricity-tariff.electricity-tariff-page',
  [PageType.EnergyKnowledgeCenterArticle]: 'api::energy.energy-knowledge-center-article',
  [PageType.EnergyKnowledgeCenterWebinar]: 'api::energy.energy-knowledge-center-webinar',
  [PageType.EnergyKnowledgeCenterWhitepaper]: 'api::energy.energy-knowledge-center-whitepaper',
  [PageType.EnergyNewsArticle]: 'api::energy.energy-news-article',
  [PageType.EnergyPage]: 'api::energy.energy-page',
  [PageType.EnergyReferenceArticle]: 'api::energy.energy-reference-article',
  [PageType.KnowledgeCenterArticle]: 'api::knowledge-center.knowledge-center-article',
  [PageType.KnowledgeCenterWebinar]: 'api::knowledge-center.knowledge-center-webinar',
  [PageType.KnowledgeCenterWhitepaper]: 'api::knowledge-center.knowledge-center-whitepaper',
  [PageType.NewsArticle]: 'api::news.news-article',
  [PageType.Page]: 'api::page.page',
  [PageType.ReferenceArticle]: 'api::reference.reference-article',
  [PageType.ShopCategoryPage]: 'api::shop-category-page.shop-category-page',
  [PageType.StyleguideArticle]: 'api::styleguide.styleguide-article',
  [PageType.StyleguidePage]: 'api::styleguide.styleguide-page',
} as const;

export const apiNameToPageTypeMap: Partial<Record<keyof Shared.ContentTypes, PageType>> = {
  'api::car.car-model': PageType.Car,
  'api::chargepilot.chargepilot-product-update-article': PageType.ChargepilotProductUpdateArticle,
  'api::electricity-tariff.electricity-tariff-blog-article': PageType.ElectricityTariffBlogArticle,
  'api::electricity-tariff.electricity-tariff-page': PageType.ElectricityTariffPage,
  'api::energy.energy-knowledge-center-article': PageType.EnergyKnowledgeCenterArticle,
  'api::energy.energy-knowledge-center-webinar': PageType.EnergyKnowledgeCenterWebinar,
  'api::energy.energy-knowledge-center-whitepaper': PageType.EnergyKnowledgeCenterWhitepaper,
  'api::energy.energy-news-article': PageType.EnergyNewsArticle,
  'api::energy.energy-page': PageType.EnergyPage,
  'api::energy.energy-reference-article': PageType.EnergyReferenceArticle,
  'api::knowledge-center.knowledge-center-article': PageType.KnowledgeCenterArticle,
  'api::knowledge-center.knowledge-center-webinar': PageType.KnowledgeCenterWebinar,
  'api::knowledge-center.knowledge-center-whitepaper': PageType.KnowledgeCenterWhitepaper,
  'api::news.news-article': PageType.NewsArticle,
  'api::page.page': PageType.Page,
  'api::reference.reference-article': PageType.ReferenceArticle,
  'api::shop-category-page.shop-category-page': PageType.ShopCategoryPage,
  'api::styleguide.styleguide-article': PageType.StyleguideArticle,
  'api::styleguide.styleguide-page': PageType.StyleguidePage,
};

export interface AliasProps {
  /**
   * Fields that should be populated as they are relevant for the content.
   */
  populateContent: string[];
  /**
   * Fields that should be copied from the source page to the alias page that are not inside of the `content` field.
   */
  fieldsToCopy: string[];
  /**
   * Whether the page has the dynamic zone `content` field.
   */
  hasContentBlocks: boolean;
}

/**
 * Info about alias locale rules for each page type or `null` if the page type doesn't support it.
 */
const knowledgeCenterArticleAliasProps: AliasProps = {
  populateContent: [],
  fieldsToCopy: ['variant', 'title', 'teaser', 'postDate', 'editDate', 'canonical'],
  hasContentBlocks: true,
};

export const pageTypeAliasProps: Record<PageType, AliasProps | null> = {
  [PageType.Page]: {
    populateContent: [],
    fieldsToCopy: ['title', 'headerTheme', 'headerThemeBelowFold', 'canonical'],
    hasContentBlocks: true,
  },
  [PageType.KnowledgeCenterArticle]: knowledgeCenterArticleAliasProps,
  [PageType.KnowledgeCenterWebinar]: {
    populateContent: [],
    fieldsToCopy: ['title', 'teaser', 'description', 'startDate', 'endDate', 'canonical'],
    hasContentBlocks: false,
  },
  [PageType.KnowledgeCenterWhitepaper]: {
    populateContent: [],
    fieldsToCopy: ['title', 'teaser', 'description', 'hideHeroArrow', 'canonical'],
    hasContentBlocks: true,
  },
  [PageType.NewsArticle]: {
    populateContent: [],
    fieldsToCopy: ['title', 'teaser', 'location', 'postDate', 'editDate', 'canonical'],
    hasContentBlocks: true,
  },
  [PageType.ReferenceArticle]: {
    populateContent: [],
    fieldsToCopy: ['variant', 'customLink', 'title', 'teaser', 'canonical'],
    hasContentBlocks: true,
  },
  [PageType.Car]: null,
  [PageType.ElectricityTariffPage]: {
    populateContent: [],
    fieldsToCopy: ['canonical'],
    hasContentBlocks: true,
  },
  [PageType.ShopCategoryPage]: {
    populateContent: [],
    fieldsToCopy: ['title', 'headerTheme', 'headerThemeBelowFold', 'canonical'],
    hasContentBlocks: true,
  },
  [PageType.StyleguideArticle]: {
    populateContent: [],
    fieldsToCopy: ['postDate', 'editDate', 'readingDuration', 'canonical'],
    hasContentBlocks: true,
  },
  [PageType.StyleguidePage]: {
    populateContent: [],
    fieldsToCopy: ['title', 'headerTheme', 'headerThemeBelowFold', 'canonical'],
    hasContentBlocks: true,
  },
  [PageType.ChargepilotProductUpdateArticle]: {
    populateContent: [],
    fieldsToCopy: ['title', 'teaser', 'postDate', 'editDate', 'canonical'],
    hasContentBlocks: true,
  },
  [PageType.ElectricityTariffBlogArticle]: {
    populateContent: [],
    fieldsToCopy: ['title', 'teaser', 'description', 'hideHeroArrow', 'canonical'],
    hasContentBlocks: true,
  },
  [PageType.EnergyPage]: {
    populateContent: [],
    fieldsToCopy: ['title', 'teaser', 'description', 'hideHeroArrow', 'canonical'],
    hasContentBlocks: true,
  },
  [PageType.EnergyNewsArticle]: {
    populateContent: [],
    fieldsToCopy: ['title', 'teaser', 'location', 'postDate', 'editDate', 'canonical'],
    hasContentBlocks: true,
  },
  [PageType.EnergyReferenceArticle]: {
    populateContent: [],
    fieldsToCopy: ['variant', 'customLink', 'title', 'teaser', 'canonical'],
    hasContentBlocks: true,
  },
  [PageType.EnergyKnowledgeCenterArticle]: knowledgeCenterArticleAliasProps,
  [PageType.EnergyKnowledgeCenterWebinar]: {
    populateContent: [],
    fieldsToCopy: ['title', 'teaser', 'description', 'startDate', 'endDate', 'canonical'],
    hasContentBlocks: false,
  },
  [PageType.EnergyKnowledgeCenterWhitepaper]: {
    populateContent: [],
    fieldsToCopy: ['title', 'teaser', 'description', 'hideHeroArrow', 'canonical'],
    hasContentBlocks: true,
  },
};

export interface ValidateSourceResponse {
  isValidUrl: boolean;
  exists: boolean;
  canCheck: boolean;
  isReachable: boolean;
  error?: { statusCode: number; message: string };
}

export const testEmail = 'tmh-cypress@mailinator.com';
